import { AdminUrls, CloudActions, FlowjoCommons } from '@portal/portal-core';
import { partition } from 'lodash-es';

const { Net } = FlowjoCommons;

const adminAllUsersActions: string[] = [
  CloudActions.ADMIN_GET_USERS_VERBOSE,
  CloudActions.ADMIN_GET_USERS_COUNT,
  CloudActions.ADMIN_GET_USERS_VERBOSE_PAGINATE,
];

// Raw shape returned by `Net.sendJson` on success: response text + HTTP status code
interface HttpResponse {
  code: number;
  data: string;
}

// Safe `succeeded`/`failed` interface wrapping an HTTP request
export interface WrapResult {
  succeeded?: boolean;
  failed?: boolean;
  data?: unknown;
  error?: unknown;
}

// Minimal shape of an action sent to the super-client API
export interface SuperClientAction {
  type?: string;
}

// Config needed to authenticate and route a super-client request
export interface SuperClientConfig {
  getAuthToken: () => string;
  baseApiUrl: string;
  timeout?: number;
}

interface WrapResultWithAction extends WrapResult {
  action: SuperClientAction;
}

// Wraps HTTP request in safe `succeeded`/`failed` interface.
// Promise.race provides a browser-native timeout guarantee independent of the
// Node.js http polyfill, which may leave the underlying XHR open under throttled
// DevTools conditions and never settle the promise.
export const wrapHttpRequest = (
  promise: Promise<HttpResponse>,
  timeoutMs = 5000,
): Promise<WrapResult> => {
  const timeoutGuard = new Promise<WrapResult>((resolve) =>
    setTimeout(
      () => resolve({ failed: true, error: new Error(`Request timed out after ${timeoutMs}ms`) }),
      timeoutMs,
    ),
  );
  return Promise.race([
    promise
      .then(({ code, data }): WrapResult =>
        code === Net.reponses.OK
          ? { succeeded: true, data: (JSON.parse(data) as { result: unknown }).result }
          : { failed: true, error: code },
      )
      .catch((err: unknown): WrapResult => ({ failed: true, error: err })),
    timeoutGuard,
  ]);
};

// Fires off a single action. Returns server response in `wrapHttpRequest` wrapper
export const sendAction = (
  config: SuperClientConfig,
  action: SuperClientAction,
): Promise<WrapResult> => {
  const authToken = config.getAuthToken();
  const headers = { Authorization: `Bearer ${authToken}` };
  const timeout = config.timeout || 5000;
  const url =
    action.type != null && adminAllUsersActions.includes(action.type)
      ? AdminUrls.adminAllUsersUrl(config.baseApiUrl)
      : AdminUrls.adminClientUrl(config.baseApiUrl);

  // Cast to the 5-arg runtime signature; the published type only exposes 4 args.
  return wrapHttpRequest(
    (Net.sendJson as (u: string, j: unknown, h: unknown, r: boolean, t: number) => Promise<HttpResponse>)(url, action, headers, true, timeout),
    timeout,
  );
};

// Fires off a sequence of actions, in order specified by `actions` array. Each action waits on its
// predecessor before firing.  Returns an object of  `succeeded` and `failed` arrays, each populated
// by corresponding requests.
export const sendActions = async (
  config: SuperClientConfig = {} as SuperClientConfig,
  actions: SuperClientAction[] = [],
): Promise<{ succeeded: WrapResultWithAction[]; failed: WrapResultWithAction[] }> => {
  // Fire actions in sequence, waiting for each to resolve
  const outcomes: WrapResult[] = [];
  for (const action of actions) {
    outcomes.push(await sendAction(config, action));
  }

  // Associate each success/failure with original action
  const withActions: WrapResultWithAction[] = outcomes.map((outcome, idx) => ({
    ...outcome,
    action: actions[idx],
  }));

  // Group by success/failure
  const [succeeded, failed] = partition(withActions, (outcome) => outcome.succeeded);

  return { succeeded, failed };
};
